import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  CbActiveConfig,
  EmailTemplate,
  EmailTemplateDetail,
  EmailTemplateListParams,
  EmailTemplateListResponse,
  EmailTemplateStatusPayload,
  WorkflowDetail,
  WorkflowOption,
} from '../types/emailTemplate'
import { normalizeWorkflowVariables } from '../utils/normalizeWorkflowVariables'

export async function getEmailTemplates(
  params: EmailTemplateListParams = {},
): Promise<EmailTemplateListResponse> {
  try {
    const response = await apiClient.get<EmailTemplateListResponse>('/api/email_templates', {
      params,
    })
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load email templates')
  }
}

export async function getEmailTemplateById(templateId: number): Promise<EmailTemplateDetail> {
  try {
    const response = await apiClient.get<EmailTemplateDetail>(`/api/email_templates/${templateId}`)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load email template')
  }
}

export async function getWorkflows(): Promise<WorkflowOption[]> {
  try {
    const response = await apiClient.get<WorkflowOption[]>('/api/workflow')
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load workflows')
  }
}

export async function getWorkflowDetail(workflowId: number): Promise<WorkflowDetail> {
  try {
    const response = await apiClient.get<WorkflowDetail | { data: WorkflowDetail }>(
      `/api/workflow/${workflowId}`,
    )
    const raw = response.data
    const detail =
      raw &&
      typeof raw === 'object' &&
      'data' in raw &&
      raw.data &&
      typeof raw.data === 'object' &&
      !Array.isArray(raw.data)
        ? raw.data
        : (raw as WorkflowDetail)

    return {
      ...detail,
      recipient_variables: normalizeWorkflowVariables(detail.recipient_variables),
      available_variables: normalizeWorkflowVariables(detail.available_variables),
    }
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load workflow details')
  }
}

export async function getCbActiveConfig(): Promise<CbActiveConfig> {
  try {
    const response = await apiClient.get<CbActiveConfig>('/api/config/cb_active')
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load config')
  }
}

/** Create or update. Matches Angular: POST /api/email_templates or /api/email_templates/:id (body excludes checkbox_data). */
export async function saveEmailTemplate(
  payload: Record<string, unknown>,
  templateId?: number,
): Promise<EmailTemplateDetail> {
  const path =
    templateId != null
      ? `/api/email_templates/${templateId}`
      : '/api/email_templates'
  try {
    const response = await apiClient.post<EmailTemplateDetail>(path, payload)
    return response.data
  } catch (error) {
    throw new Error(
      extractErrorMessage(error) || (templateId ? 'Failed to save email template' : 'Failed to create email template'),
    )
  }
}

/** Test send (Angular: POST /api/email_templates/sendMail). */
export async function sendEmailTemplateTestMail(body: {
  workflow: string
  toAddress: string
  bodyParams: Record<string, string>
  testsms?: '1'
}): Promise<void> {
  try {
    await apiClient.post('/api/email_templates/sendMail', body)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to send test message')
  }
}

export async function updateEmailTemplateStatus(
  templateId: number,
  payload: EmailTemplateStatusPayload,
): Promise<EmailTemplate> {
  try {
    const response = await apiClient.post<EmailTemplate>(
      `/api/email_templates/${templateId}`,
      payload,
    )
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to update template status')
  }
}
